import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import { useAuth } from '../contexts/AuthContext';

/**
 * May this user write a settings endpoint gated `requireRole('admin')`?
 *
 * The API's non-strict `requireRole('admin')` admits two callers:
 *
 *   - role `admin`, literally. NOT `owner` — the list is matched as written,
 *     so `isAdminRole`, which admits `owner`, would show an owner controls
 *     that 403.
 *   - role `dispatcher`, when the company has `dispatcher_admin_access` on.
 *
 * `dispatcher_admin_access` is not on /auth/me, so it is read from
 * GET /settings/roles, which carries no role gate. Same key and shape as
 * RolesPermissionsPage and ApprovalQueueScreen (both read the same object
 * out of the envelope), so the three share one cache entry and a toggle
 * there is seen here. Only a dispatcher pays for the query.
 *
 * While the flag is loading a dispatcher reads as NOT allowed, so controls
 * appear rather than being shown and then taken away.
 *
 * Use it only where the write is non-strict `requireRole('admin')`. A route
 * with `{ strict: true }`, or with a wider role list, needs its own gate.
 */
export function useCanWriteCompanySettings(): boolean {
  const { user } = useAuth();
  const role = user?.role;

  const { data: rolesSettings } = useQuery({
    queryKey: ['settings', 'roles'],
    queryFn: async () => {
      const res = await api.settingsRoles.get();
      return unwrap<{ dispatcher_admin_access?: boolean }>(res.data) ?? {};
    },
    enabled: role === 'dispatcher',
  });

  if (role === 'admin') return true;
  return role === 'dispatcher' && rolesSettings?.dispatcher_admin_access === true;
}
